/**
 * The theme-art background layer shared by the public profile hero
 * (`ProfileHero`) and the Settings "Live Preview" (`ProfileAppearancePreview`)
 * — extracted so both read as the same material rather than one forking the
 * other's markup. Renders nothing when no background is selected.
 *
 * Kept as its own element (not a `background` on the panel) so it alone can
 * carry a `filter`: the wash lifts and warms the art while leaving every
 * foreground element — name, stats, controls — at full strength. See the
 * inline comments below for why each layer exists; this is a verbatim
 * extraction of what `ProfileHero` always rendered, not a redesign.
 */
export interface HeroThemeBackdropProps {
  /** Resolved background image URL, or `null` to render nothing. */
  backgroundUrl: string | null;
}

export function HeroThemeBackdrop({ backgroundUrl }: HeroThemeBackdropProps) {
  if (!backgroundUrl) return null;

  return (
    <div
      aria-hidden="true"
      className="pointer-events-none absolute inset-0 z-0 overflow-hidden rounded-[inherit]"
    >
      {/* `sepia` before `saturate` is what keeps the result *warm* rather
          than grey: desaturating a moonlit blue interior on its own lands it
          on neutral grey, which then reads as a faded photograph rather than
          as ink on paper. Pushing it through sepia first moves the whole
          scene onto the parchment's own amber axis, and only then is the
          saturation and contrast pulled down. Confined to this layer — no
          profile UI is ever filtered. */}
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat [filter:sepia(1)_saturate(0.95)_contrast(0.58)_brightness(1.34)]"
        style={{ backgroundImage: `url("${backgroundUrl}")` }}
      />
      {/* A warm tint, multiplied. `sepia()` above can only warm what has some
          colour in it; the theme backgrounds are lit stone interiors whose
          mid-tones are already near-neutral, and those came through as grey
          however far the filter was pushed. Multiplying an amber through
          them tints the greys without flattening the scene, which is what
          makes the whole panel read as one warm material rather than as a
          warm left half and a grey right half. */}
      <div className="absolute inset-0 bg-[rgb(246_226_186)] opacity-40 mix-blend-multiply" />
      {/* The parchment veil. Two layers, because the panel is not one flat
          beige rectangle: a heavy band over the identity end that falls away
          well before the middle, over a flat wash carrying the whole panel.
          Both are the `parchment` token colour, so the hero, the page plate
          and the game cards' stat panels are plainly the same material
          (docs/UI_GUIDELINES.md §2). The far right keeps the least veil, so
          the architecture there stays legible. */}
      <div className="absolute inset-0 bg-[linear-gradient(90deg,rgb(255_244_216/0.66)_0%,rgb(255_244_216/0.5)_22%,rgb(255_244_216/0.26)_50%,rgb(255_244_216/0.14)_76%,rgb(255_244_216/0.1)_100%)]" />
      <div className="absolute inset-0 bg-[rgb(255_244_216/0.34)]" />
      {/* A vertical lift as well as the horizontal one. Theme backgrounds are
          lit rooms: the upper third is ceiling and shadow and comes out
          several stops darker than the floor, so a purely horizontal wash
          leaves a grey band running behind the heading while the parchment
          reads correctly below it. */}
      <div className="absolute inset-0 bg-[linear-gradient(to_bottom,rgb(255_244_216/0.22),rgb(255_244_216/0)_58%)]" />
      {/* The same paper grain the app's other parchment surfaces carry, so
          the wash reads as a sheet the scene was printed into rather than as
          an opacity slider. Multiplied in at a low dose: at full strength the
          tile is warm enough to turn the whole panel tan. */}
      <div className="px-paper-grain absolute inset-0 opacity-30 mix-blend-multiply" />
    </div>
  );
}
